/* ==========================================================================
   KOLP — Bundle pricing (isolated, pure, framework-free)

   This is the SINGLE SOURCE OF TRUTH for how a KOLP pack is priced in the
   storefront UI. It is intentionally dependency-free so it can be unit
   tested in Node (see scripts/test-bundle-pricing.mjs) and imported by the
   storefront, the cart drawer and — conceptually — mirrored by the Shopify
   Cart Transform function (shopify-functions/bundle-discount/).

   All money values are integers in MINOR units (cents). €24.90 -> 2490.

   IMPORTANT: The prices here drive DISPLAY. Real checkout enforcement
   requires the Shopify function to be deployed OR a native Bundles app.
   See docs/BUNDLE-PRICING.md.
   ========================================================================== */

(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.KOLPPricing = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* Default pack configuration. The storefront overrides this from theme
     settings via KOLPPricing.configure(). Keep qty 1 as the reference unit. */
  var DEFAULT_CONFIG = {
    currency: 'EUR',
    symbol: '€',
    /* Live conversion rate from the shop's base currency (EUR — what every
       price/threshold below is denominated in) to `currency`, the buyer's
       active presentment currency. The storefront sets this from
       window.Shopify.currency.rate (1 in EUR markets, e.g. ~390 in Hungary).
       Left at 1 by default so every existing caller/test is unaffected
       until the storefront actually configures a non-EUR market. */
    currencyRate: 1,
    freeShippingThreshold: 6900,
    /* true when freeShippingThreshold is already in the active presentment
       currency (e.g. the Hungary market's own HUF threshold) and must not be
       run through the FX conversion again. */
    freeShippingThresholdIsPresentment: false,
    referenceUnitPrice: 0,
    /* Whether this product line has any reference/"compare-at" concept at
       all (drives the crossed-out price + savings badge). True for the
       Theme-settings tee flow (a genuine, configured bulk-savings message).
       False for a per-product pack_prices metafield flow (e.g. boxers, which
       has no reference price of its own) -- without this flag, fullUnitPrice()
       falls back to the real unitPrice() whenever referenceUnitPrice is 0/
       falsy, and since a bulk pack is *always* cheaper per-unit than the
       single price by design, that fallback alone produced a bogus "savings"
       badge on every boxers pack tier (e.g. crossing out a nonsense inflated
       price) instead of showing no badge at all, as intended. */
    hasReferencePrice: true,
    packs: [
      { qty: 1, price: 2490, name: '1 T-shirt', badge: '' },
      { qty: 3, price: 5490, name: '3-Pack', badge: '' },
      { qty: 5, price: 7490, name: '5-Pack', badge: 'MOST POPULAR' },
      { qty: 7, price: 9490, name: '7-Pack', badge: 'BEST VALUE' }
    ]
  };

  var config = clone(DEFAULT_CONFIG);

  function clone(o) { return JSON.parse(JSON.stringify(o)); }

  function configure(partial) {
    if (!partial) return config;
    if (partial.packs && Array.isArray(partial.packs) && partial.packs.length) {
      config.packs = partial.packs
        .map(function (p) {
          // variantId (optional): the pack-parent variant priced at the pack
          // total. When present, the pack is added to the cart as ONE line item
          // of this variant with the per-item choices as properties.
          var vid = p.variantId != null && p.variantId !== '' ? parseInt(p.variantId, 10) : 0;
          // presentmentPrice (optional): the pack-parent variant's own real,
          // already-in-the-active-currency price (set server-side from the
          // variant itself, e.g. a fixed Market price-list override) --
          // preferred by packTotal() below over `price` (a separately
          // maintained base-currency estimate). Must be forwarded here or
          // it's silently dropped and every non-base-currency market falls
          // back to converting the stale estimate instead.
          var pp = p.presentmentPrice != null ? parseInt(p.presentmentPrice, 10) : NaN;
          // presentmentComparePrice (optional): the pack-parent variant's own
          // real compare_at_price, already in the active currency -- the
          // genuine per-pack "was" price a merchant configured on that
          // product, as opposed to a synthetic unit-price x qty estimate
          // (which for a *cheaper-per-unit-by-design* bulk pack always
          // produces SOME positive "savings" number whether or not a real
          // reference price was ever set). Preferred by savings() below.
          var pc = p.presentmentComparePrice != null ? parseInt(p.presentmentComparePrice, 10) : NaN;
          return {
            qty: parseInt(p.qty, 10),
            price: parseInt(p.price, 10),
            name: p.name || (p.qty + '-Pack'),
            badge: p.badge || '',
            variantId: vid > 0 ? vid : null,
            presentmentPrice: isFinite(pp) ? pp : null,
            presentmentComparePrice: isFinite(pc) && pc > 0 ? pc : null
          };
        })
        .filter(function (p) { return p.qty > 0 && p.price >= 0; })
        .sort(function (a, b) { return a.qty - b.qty; });
    }
    if (partial.freeShippingThresholdIsPresentment != null) {
      config.freeShippingThresholdIsPresentment = partial.freeShippingThresholdIsPresentment === true || partial.freeShippingThresholdIsPresentment === 'true';
    }
    if (partial.freeShippingThreshold != null) {
      config.freeShippingThreshold = parseInt(partial.freeShippingThreshold, 10) || 0;
    }
    if (partial.referenceUnitPrice != null) {
      config.referenceUnitPrice = parseInt(partial.referenceUnitPrice, 10) || 0;
    }
    if (partial.hasReferencePrice != null) {
      config.hasReferencePrice = !!partial.hasReferencePrice;
    }
    // The qty:1 tier's own `price` above is a separately maintained EUR
    // estimate (Theme setting or per-product metafield) that can drift from
    // what the underlying product variant actually charges -- singleItem-
    // PresentmentPrice is that real, already-in-the-active-currency price
    // (Shopify's own number, from source_product.price), preferred for
    // qty:1 display whenever it's given. No such authoritative source
    // exists for qty>1 packs (a genuinely separate discounted price, not
    // "the product's price"), so this only ever touches the qty:1 entry.
    if (partial.singleItemPresentmentPrice != null) {
      var real = parseInt(partial.singleItemPresentmentPrice, 10);
      if (isFinite(real) && real >= 0) {
        var single = config.packs.find(function (p) { return p.qty === 1; });
        if (single) single.presentmentPrice = real;
      }
    }
    // Same idea as singleItemPresentmentPrice, for the qty:1 tier's own real
    // compare_at_price (a source_product-level Shopify field), not a
    // metafield/theme-setting estimate.
    if (partial.singleItemPresentmentComparePrice != null) {
      var realCompare = parseInt(partial.singleItemPresentmentComparePrice, 10);
      if (isFinite(realCompare) && realCompare > 0) {
        var single2 = config.packs.find(function (p) { return p.qty === 1; });
        if (single2) single2.presentmentComparePrice = realCompare;
      }
    }
    if (partial.symbol) config.symbol = partial.symbol;
    if (partial.currency) config.currency = partial.currency;
    if (partial.currencyRate != null) {
      var rate = parseFloat(partial.currencyRate);
      config.currencyRate = (isFinite(rate) && rate > 0) ? rate : 1;
    }
    return config;
  }

  function getConfig() { return config; }

  /* Convert a base-currency (EUR) minor-unit amount into the buyer's active
     presentment currency's minor units, using the live rate. All pack/
     threshold prices are stored in `config` in base currency and pass
     through here on every read, so calling configure({currencyRate}) at
     any point — before or after packs are set — is safe. */
  function toPresentment(baseMinor) {
    var n = Number(baseMinor) || 0;
    return config.currencyRate === 1 ? n : Math.round(n * config.currencyRate);
  }

  function unitPrice() {
    var p1 = config.packs.find(function (p) { return p.qty === 1; });
    if (p1 && p1.presentmentPrice != null) return p1.presentmentPrice;
    var base = p1 ? p1.price : (config.packs[0] ? config.packs[0].price : 0);
    return toPresentment(base);
  }

  /* Reference ("compare-at") unit price used for the crossed-out price and
     savings %, including on the single-item tier. Falls back to the real
     unit price (no discount shown on the single item, but qty>1 tiers can
     still show a genuine bulk-savings badge) when unconfigured. Returns 0
     (no badge at all, on any tier) when hasReferencePrice is false — e.g.
     products priced via a per-product pack_prices metafield, which have no
     reference-price concept of their own; unitPrice()'s fallback would
     otherwise always show a "savings" on every qty>1 tier purely because a
     bulk pack is cheaper per-unit than the single price by design, not
     because any real reference/compare-at was ever configured. */
  function fullUnitPrice() {
    if (!config.hasReferencePrice) return 0;
    return config.referenceUnitPrice ? toPresentment(config.referenceUnitPrice) : unitPrice();
  }

  function getPack(qty) {
    return config.packs.find(function (p) { return p.qty === Number(qty); }) || null;
  }

  /* Total price for a pack of `qty`, in the active presentment currency.
     Falls back to linear pricing off the unit price if that exact pack
     size is not configured. */
  function packTotal(qty) {
    var pack = getPack(qty);
    if (pack) return pack.presentmentPrice != null ? pack.presentmentPrice : toPresentment(pack.price);
    return unitPrice() * Number(qty);
  }

  /* The free-shipping threshold in the active presentment currency. */
  function freeShippingThreshold() {
    if (config.freeShippingThresholdIsPresentment) return Number(config.freeShippingThreshold) || 0;
    return toPresentment(config.freeShippingThreshold);
  }

  /* Rounding step (minor units) for the displayed per-shirt/per-pair price.
     The figure must stay TRUE ("only X / piece"), so it is only rounded to
     the currency's smallest unit actually in use: HUF has no fillér, so
     whole forints (100 minor units); everything else keeps real cents.
     (An earlier nearest-50-Ft / whole-zloty cosmetic step overstated e.g.
     23 980 Ft / 3 = 7 993 Ft as "8 000 Ft".) */
  function perUnitRoundingStep() {
    if (config.currency === 'HUF') return 100;
    return 1;
  }

  /* Per-shirt effective price, rounded to the nearest cent for display (or
     the nearest perUnitRoundingStep(), for currencies that want coarser
     rounding — see above). */
  function pricePerShirt(qty) {
    if (!qty) return 0;
    var raw = packTotal(qty) / qty;
    // A single item's "per unit" price IS its price: never round it to a
    // coarser step (11 990 Ft must not display as "12 000 Ft / piece").
    if (qty === 1) return Math.round(raw);
    var step = perUnitRoundingStep();
    return step > 1 ? Math.round(raw / step) * step : Math.round(raw);
  }

  /* Absolute + relative saving of a pack vs its own real compare_at_price
     when the pack-parent product has one configured (the genuine, merchant-
     set "was" price for that exact pack size) — falls back to the synthetic
     unitPrice x qty estimate (gated by hasReferencePrice) only when no real
     compare price exists for this pack. Preferring the real price matters:
     a synthetic qty x unitPrice comparison against a bulk pack's (by design,
     lower per-unit) total always finds SOME "discount" whether or not any
     real reference price was ever configured for that product line. */
  function savings(qty) {
    var pack = getPack(qty);
    var full = pack && pack.presentmentComparePrice != null
      ? pack.presentmentComparePrice
      : fullUnitPrice() * Number(qty);
    var total = packTotal(qty);
    var abs = Math.max(0, full - total);
    return {
      amount: abs,
      percent: full > 0 ? Math.round((abs / full) * 100) : 0,
      fullPrice: full
    };
  }

  /* `amountMinor` is expected already in the active presentment currency —
     either the output of packTotal()/pricePerShirt() above, or a real
     Shopify cart amount (which Shopify itself already converts server-
     side), so no further conversion happens here. */
  function qualifiesForFreeShipping(amountMinor) {
    return Number(amountMinor) >= freeShippingThreshold();
  }

  function freeShippingRemaining(amountMinor) {
    return Math.max(0, freeShippingThreshold() - Number(amountMinor));
  }

  /* Format minor units (already in the active presentment currency — see
     the functions above) as a currency string.

     For the base currency (EUR, the common case and every existing test)
     this keeps the exact original deterministic formatting: symbol-prefix,
     dot thousands, comma decimal — so it doesn't depend on Intl/locale data.

     For any other active currency (a converted Shopify Market, e.g. HUF in
     Hungary) it defers to Intl.NumberFormat, which gets the symbol,
     decimal places (HUF has none, EUR has two) and grouping right per
     currency without KOLP having to hand-maintain a table of them. */
  function formatMoney(minor) {
    var n = Number(minor) || 0;
    var sign = n < 0 ? '-' : '';
    var abs = Math.abs(n);
    var currency = config.currency || 'EUR';
    if (currency === 'HUF') {
      // Mirror Shopify's own HUF display for this store ("7.400 Ft": no
      // decimals, "." thousands separator, symbol after the amount) so the
      // prices this JS renders match the Liquid `money` output around them.
      var ft = String(Math.round(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return sign + ft + ' Ft';
    }
    if (currency === 'CZK') {
      // Same idea for the Czechia market: Shopify prints CZK on this store
      // as "1.295,00 Kč" ("." thousands, "," decimals, symbol after).
      var kcWhole = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      var kcCents = String(abs % 100).padStart(2, '0');
      return sign + kcWhole + ',' + kcCents + ' Kč';
    }
    if (currency === 'PLN') {
      // Poland market: Shopify prints PLN on this store as "1.295,00 zł".
      var zlWhole = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      var zlCents = String(abs % 100).padStart(2, '0');
      return sign + zlWhole + ',' + zlCents + ' zł';
    }
    if (currency === 'RON') {
      // Romania market: "1.295,00 lei" (dot thousands, comma decimals, suffix).
      var leiWhole = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      var leiCents = String(abs % 100).padStart(2, '0');
      return sign + leiWhole + ',' + leiCents + ' lei';
    }
    if (currency !== 'EUR' && typeof Intl !== 'undefined' && Intl.NumberFormat) {
      try {
        var lang = (typeof document !== 'undefined' && document.documentElement && document.documentElement.lang) || undefined;
        return new Intl.NumberFormat(lang, {
          style: 'currency',
          currency: currency
        }).format(n / 100);
      } catch (e) {
        // Unknown/invalid currency code — fall through to the legacy format.
      }
    }
    var whole = Math.floor(abs / 100);
    var cents = String(abs % 100).padStart(2, '0');
    var wholeStr = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return sign + config.symbol + ' ' + wholeStr + ',' + cents;
  }

  /* Build the line items payload for Shopify's /cart/add.js from a bundle
     selection. `selection` = array of { variantId, color, size } of length qty.
     `variantMap` optional: fn(color,size) -> variantId when not pre-resolved. */
  function buildCartItems(selection, opts) {
    opts = opts || {};
    var qty = selection.length;
    var pack = getPack(qty);
    var offerName = opts.offerName || (pack ? ('KOLP ' + pack.name) : ('KOLP ' + qty + '-Pack'));
    var bundleId = opts.bundleId || generateBundleId();
    var perShirt = pricePerShirt(qty);
    var noun = opts.itemNoun || 'shirt';
    var nounPlural = opts.itemNounPlural || (noun + 's');
    var nounCap = opts.nounCap || (noun.charAt(0).toUpperCase() + noun.slice(1));
    var packVariantId = Number(opts.packVariantId || (pack && pack.variantId) || 0);

    // Preferred path: a pack-parent variant priced at the pack total exists.
    // Add ONE line item; the per-item choices ride along as line-item
    // properties, so the cart & checkout show a single correctly-priced line.
    if (packVariantId > 0) {
      var props = {
        _bundle_id: bundleId,
        _bundle_qty: String(qty),
        _bundle_offer: offerName,
        _bundle_noun: noun,
        _bundle_noun_plural: nounPlural,
        _bundle_total_price: String(packTotal(qty))
      };
      selection.forEach(function (shirt, i) {
        props[nounCap + ' ' + (i + 1)] = shirt.color + ' / ' + shirt.size;
      });
      return [{ id: packVariantId, quantity: 1, properties: props }];
    }

    // Fallback: no parent variant configured (e.g. the 1-pack tier). Add each
    // item as its own variant line — the cart drawer groups them for display
    // and a Cart Transform function (if deployed) reprices them.
    return selection.map(function (shirt, i) {
      var props = {
        _bundle_id: bundleId,
        _bundle_qty: String(qty),
        _bundle_offer: offerName,
        _bundle_position: String(i + 1),
        _bundle_unit_price: String(perShirt),
        _bundle_total_price: String(packTotal(qty)),
        _bundle_noun: noun,
        _bundle_noun_plural: nounPlural
      };
      // Human-readable, shown in Shopify admin + emails.
      var lineLabel = (opts.lineLabel || '__OFFER__ — __NOUN__ __N__ of __QTY__')
        .replace('__OFFER__', offerName).replace('__NOUN__', noun).replace('__N__', String(i + 1)).replace('__QTY__', String(qty));
      props[opts.lineLabelKey || 'Bundle'] = lineLabel;
      return {
        id: Number(shirt.variantId),
        quantity: 1,
        properties: props
      };
    });
  }

  function generateBundleId() {
    var t = (typeof Date !== 'undefined' && Date.now) ? Date.now().toString(36) : '0';
    var r = Math.random().toString(36).slice(2, 8);
    return 'kolp-' + t + '-' + r;
  }

  /* Given raw Shopify cart line items, group them by _bundle_id for display in
     the cart drawer. Standalone (non-bundle) lines are returned in `loose`.
     Pack pricing is always real by the time a line reaches the cart (a
     pack-parent variant priced at the pack total, or a single item), so the
     group total is simply what Shopify reports — no phantom discount. */
  function groupCart(items) {
    var groups = {};
    var loose = [];
    (items || []).forEach(function (line) {
      var props = line.properties || {};
      var bid = props._bundle_id;
      if (!bid) { loose.push(line); return; }
      if (!groups[bid]) {
        // Prefer the explicit _bundle_noun(_plural) properties buildCartItems
        // now stamps on every line. Regex-parsing the noun back out of the
        // human-readable "Bundle" text (older lines only have that) used to
        // be the only source, but [a-z-]+ silently failed to match
        // translated/accented nouns like "Póló" and fell back to the
        // hardcoded English 'item' -- kept here only as a last-resort
        // fallback for lines added before this property existed.
        var nounMatch = /—\s*([^\s\d]+)\s+\d+\s+of\s+\d+/i.exec(props.Bundle || '');
        groups[bid] = {
          bundleId: bid,
          offer: props._bundle_offer || 'KOLP Pack',
          qty: parseInt(props._bundle_qty, 10) || 0,
          noun: props._bundle_noun || (nounMatch ? nounMatch[1].toLowerCase() : 'item'),
          nounPlural: props._bundle_noun_plural || (nounMatch ? nounMatch[1].toLowerCase() + 's' : 'items'),
          // a pack added as ONE pack-parent line carries no _bundle_position
          isParentLine: props._bundle_position == null,
          lines: [],
          shopifyTotal: 0
        };
      }
      groups[bid].lines.push(line);
      groups[bid].shopifyTotal += (line.final_line_price != null
        ? line.final_line_price
        : (line.price || 0) * (line.quantity || 1));
    });

    var list = Object.keys(groups).map(function (k) {
      var g = groups[k];
      var count = g.lines.reduce(function (s, l) { return s + (l.quantity || 1); }, 0);
      g.count = g.isParentLine ? (g.qty || count) : count;
      g.intendedTotal = g.shopifyTotal;
      g.discount = 0;
      return g;
    });

    return { bundles: list, loose: loose };
  }

  /* The cart subtotal in minor units, from what Shopify reports. Drives the
     free-shipping progress bar. */
  function intendedSubtotal(items) {
    var grouped = groupCart(items);
    var total = 0;
    grouped.bundles.forEach(function (b) { total += b.intendedTotal; });
    grouped.loose.forEach(function (l) {
      total += (l.final_line_price != null ? l.final_line_price : (l.price || 0) * (l.quantity || 1));
    });
    return total;
  }

  return {
    DEFAULT_CONFIG: DEFAULT_CONFIG,
    configure: configure,
    getConfig: getConfig,
    unitPrice: unitPrice,
    fullUnitPrice: fullUnitPrice,
    getPack: getPack,
    packTotal: packTotal,
    pricePerShirt: pricePerShirt,
    savings: savings,
    qualifiesForFreeShipping: qualifiesForFreeShipping,
    freeShippingRemaining: freeShippingRemaining,
    freeShippingThreshold: freeShippingThreshold,
    formatMoney: formatMoney,
    buildCartItems: buildCartItems,
    generateBundleId: generateBundleId,
    groupCart: groupCart,
    intendedSubtotal: intendedSubtotal
  };
});
